import { zodResolver } from '@hookform/resolvers/zod';
import { type EventFeedResponse, Memo, type MemoList } from '@kalos/kalos-rpc';
import {
  Button,
  Checkbox,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Textarea,
  toast,
} from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { LockIcon, PinIcon } from 'lucide-react';
import { type FC, useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { queryKeys } from '../../../../../hooks/react-query/constants';
import { useUpdateMemoMutation } from '../../../../../hooks/react-query/useMemoQuery';
import { type ZodObjectWithModelKeys } from '../../../../../tools/typeguargs';

type EditMemoFormFields = {
  description: string;
  isPinned: boolean;
  isInternal: boolean;
};

const editMemoFormSchema = z.object({
  description: z.string().max(5000, 'Description is too long (max 5000 characters)'),
  isPinned: z.boolean(),
  isInternal: z.boolean(),
}) satisfies ZodObjectWithModelKeys<EditMemoFormFields>;

type EditMemoFormSchemaType = z.infer<typeof editMemoFormSchema>;

interface EditMemoFormProps {
  memo: Memo;
  onClose?: () => void;
  onSuccess?: () => void;
}

export const EditMemoForm: FC<EditMemoFormProps> = ({ memo, onClose, onSuccess }) => {
  const { mutateAsync: updateMemo, isPending } = useUpdateMemoMutation();
  const queryClient = useQueryClient();

  const form = useForm<EditMemoFormSchemaType>({
    resolver: zodResolver(editMemoFormSchema),
    mode: 'onBlur',
    defaultValues: {
      description: memo.description || '',
      isPinned: memo.isPinned || false,
      isInternal: memo.isInternal ?? true,
    },
  });

  // Reset form when memo prop changes to ensure we have the latest data
  useEffect(() => {
    form.reset({
      description: memo.description || '',
      isPinned: memo.isPinned || false,
      isInternal: memo.isInternal ?? true,
    });
  }, [memo, form]);

  const handleSubmit: SubmitHandler<EditMemoFormSchemaType> = async (data) => {
    const updatedMemo = Memo.create({
      ...memo,
      description: data.description,
      isPinned: data.isPinned,
      isInternal: data.isInternal,
    });

    // Check if isPinned changed - if so, we need to refetch to get proper sorting
    const isPinnedChanged = (memo.isPinned || false) !== data.isPinned;

    // Only do optimistic updates if isPinned didn't change
    if (!isPinnedChanged) {
      // Optimistically update the query cache
      queryClient.setQueryData<MemoList>(
        [queryKeys.memo.root, queryKeys.memo.getMemoHash({ id: memo.id })],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            results: [updatedMemo],
          };
        },
      );

      // Also update the memo list cache
      queryClient.setQueriesData<MemoList>({ queryKey: [queryKeys.memoList.root] }, (oldData) => {
        if (!oldData?.results) return oldData;
        return {
          ...oldData,
          results: oldData.results.map((m) => (m.id === memo.id ? updatedMemo : m)),
        };
      });

      // Update event feed cache
      queryClient.setQueriesData<EventFeedResponse>(
        { queryKey: [queryKeys.eventFeed.root] },
        (oldData) => {
          if (!oldData?.results) return oldData;
          return {
            ...oldData,
            results: oldData.results.map((item) =>
              item.feedType === 'memo' && item.sourceId === memo.id
                ? {
                    ...item,
                    content: data.description,
                    isPinned: data.isPinned,
                    isInternal: data.isInternal,
                  }
                : item,
            ),
          };
        },
      );
    }

    try {
      await updateMemo({
        ...updatedMemo,
        fieldMask: ['Description', 'IsPinned', 'IsInternal'],
      });

      toast({
        title: 'Note updated successfully',
        description: 'Your changes have been saved',
      });

      onSuccess?.();
      onClose?.();

      // If isPinned changed, invalidate queries to refetch with proper sorting
      // Do this after closing the dialog so it doesn't block the UI
      if (isPinnedChanged) {
        queryClient.invalidateQueries({ queryKey: [queryKeys.memo.root] });
        queryClient.invalidateQueries({ queryKey: [queryKeys.memoList.root] });
        queryClient.invalidateQueries({ queryKey: [queryKeys.eventFeed.root] });
      }
    } catch (error) {
      console.error('Failed to update memo:', error);

      // Rollback optimistic updates on error
      queryClient.invalidateQueries({ queryKey: [queryKeys.memo.root] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.memoList.root] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.eventFeed.root] });

      toast({
        title: 'Failed to update note',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormContext {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-5 py-6">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Note</FormLabel>
              <FormMessage />
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter your note..."
                  disabled={isPending}
                  maxLength={5000}
                  className="min-h-[120px] resize-y"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex flex-wrap gap-x-6">
          <FormField
            control={form.control}
            name="isPinned"
            render={({ field }) => (
              <FormItem className="touch:min-h-11 flex items-center gap-2 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isPending}
                  />
                </FormControl>
                <FormLabel className="touch:min-h-11 flex cursor-pointer items-center gap-1 font-normal">
                  <PinIcon className="h-4 w-4" />
                  Pin this note
                </FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isInternal"
            render={({ field }) => (
              <FormItem className="touch:min-h-11 flex items-center gap-2 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isPending}
                  />
                </FormControl>
                <FormLabel className="touch:min-h-11 flex cursor-pointer items-center gap-1 font-normal">
                  <LockIcon className="h-4 w-4" />
                  Internal only
                </FormLabel>
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-3">
          {onClose && (
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </FormContext>
  );
};
